import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import "./LocationSelector.css";

const DEFAULT_LEVELS = ["state", "city", "area"];
const EMPTY_VALUE = {
  country: "",
  state: "",
  city: "",
  area: "",
};
const PAGE_REQUEST = {
  page_number: 1,
  page_size: 100,
  order_by: "ASC",
};

const LocationSelector = ({
  value = EMPTY_VALUE,
  onChange = () => {},
  levels = DEFAULT_LEVELS,
  onOptionsChange = () => {},
}) => {
  const authState = useSelector((state) => state.auth.user);
  const token = authState?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);

  const selectedValue = useMemo(
    () => ({
      ...EMPTY_VALUE,
      ...value,
    }),
    [value]
  );

  const shouldShowState = levels.includes("state");
  const shouldShowCity = levels.includes("city");
  const shouldShowArea = levels.includes("area");

  const getAuthConfig = useMemo(() => {
    if (!token) {
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, [token]);

  const emitChange = (nextValue) => {
    onChange({
      ...EMPTY_VALUE,
      ...nextValue,
    });
  };

  const fetchCountries = async () => {
    setIsLoadingCountries(true);

    try {
      const response = await axios.post(
        `${baseUrl}/admin/countries`,
        PAGE_REQUEST,
        getAuthConfig
      );
      setCountries(response.data?.countries || []);
    } catch (error) {
      setCountries([]);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }

    setIsLoadingStates(true);

    try {
      const response = await axios.post(
        `${baseUrl}/admin/states/${countryId}`,
        PAGE_REQUEST,
        getAuthConfig
      );
      setStates(response.data?.states || []);
    } catch (error) {
      setStates([]);
    } finally {
      setIsLoadingStates(false);
    }
  };

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }

    setIsLoadingCities(true);

    try {
      const response = await axios.post(
        `${baseUrl}/admin/cities/${stateId}`,
        PAGE_REQUEST,
        getAuthConfig
      );
      setCities(response.data?.cities || []);
    } catch (error) {
      setCities([]);
    } finally {
      setIsLoadingCities(false);
    }
  };

  const fetchAreas = async (cityId) => {
    if (!cityId) {
      setAreas([]);
      return;
    }

    setIsLoadingAreas(true);

    try {
      const response = await axios.post(
        `${baseUrl}/admin/areas/${cityId}`,
        PAGE_REQUEST,
        getAuthConfig
      );
      setAreas(response.data?.areas || []);
    } catch (error) {
      setAreas([]);
    } finally {
      setIsLoadingAreas(false);
    }
  };

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;

    emitChange({
      ...selectedValue,
      country: nextCountry,
      state: "",
      city: "",
      area: "",
    });
  };

  const handleStateChange = (event) => {
    const nextState = event.target.value;

    emitChange({
      ...selectedValue,
      state: nextState,
      city: "",
      area: "",
    });
  };

  const handleCityChange = (event) => {
    const nextCity = event.target.value;

    emitChange({
      ...selectedValue,
      city: nextCity,
      area: "",
    });
  };

  const handleAreaChange = (event) => {
    emitChange({
      ...selectedValue,
      area: event.target.value,
    });
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!countries.length || selectedValue.country) {
      return;
    }

    const indiaCountry = countries.find((country) => country.name === "India");
    const defaultCountryId = indiaCountry?.id || countries[0]?.id || "";

    if (!defaultCountryId) {
      return;
    }

    emitChange({
      ...selectedValue,
      country: String(defaultCountryId),
    });
  }, [countries, selectedValue, onChange]);

  useEffect(() => {
    fetchStates(selectedValue.country);
  }, [selectedValue.country]);

  useEffect(() => {
    fetchCities(selectedValue.state);
  }, [selectedValue.state]);

  useEffect(() => {
    fetchAreas(selectedValue.city);
  }, [selectedValue.city]);

  useEffect(() => {
    if (!states.length || !selectedValue.state) {
      return;
    }

    const isSelectedStateValid = states.some(
      (state) => String(state.id) === String(selectedValue.state)
    );

    if (!isSelectedStateValid) {
      emitChange({
        ...selectedValue,
        state: "",
        city: "",
        area: "",
      });
    }
  }, [states, selectedValue, onChange]);

  useEffect(() => {
    if (!cities.length || !selectedValue.city) {
      return;
    }

    const isSelectedCityValid = cities.some(
      (city) => String(city.id) === String(selectedValue.city)
    );

    if (!isSelectedCityValid) {
      emitChange({
        ...selectedValue,
        city: "",
        area: "",
      });
    }
  }, [cities, selectedValue, onChange]);

  useEffect(() => {
    if (!areas.length || !selectedValue.area) {
      return;
    }

    const isSelectedAreaValid = areas.some(
      (area) => String(area.id) === String(selectedValue.area)
    );

    if (!isSelectedAreaValid) {
      emitChange({
        ...selectedValue,
        area: "",
      });
    }
  }, [areas, selectedValue, onChange]);

  useEffect(() => {
    onOptionsChange({
      countries,
      states,
      cities,
      areas,
      selectedCountry:
        countries.find(
          (country) => String(country.id) === String(selectedValue.country)
        ) || null,
      selectedState:
        states.find((state) => String(state.id) === String(selectedValue.state)) ||
        null,
      selectedCity:
        cities.find((city) => String(city.id) === String(selectedValue.city)) ||
        null,
      selectedArea:
        areas.find((area) => String(area.id) === String(selectedValue.area)) ||
        null,
    });
  }, [
    countries,
    states,
    cities,
    areas,
    selectedValue.country,
    selectedValue.state,
    selectedValue.city,
    selectedValue.area,
    onOptionsChange,
  ]);

  return (
    <div className="lsel">
      <div className="lsel__grid">
        <label className="lsel__field">
          <span className="lsel__label">Country</span>
          <select
            className="lsel__select"
            value={selectedValue.country}
            onChange={handleCountryChange}
            disabled={isLoadingCountries}
          >
            <option value="">Select country</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </label>

        {shouldShowState ? (
          <label className="lsel__field">
            <span className="lsel__label">State</span>
            <select
              className="lsel__select"
              value={selectedValue.state}
              onChange={handleStateChange}
              disabled={!selectedValue.country || isLoadingStates}
            >
              <option value="">Select state</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {shouldShowCity ? (
          <label className="lsel__field">
            <span className="lsel__label">City</span>
            <select
              className="lsel__select"
              value={selectedValue.city}
              onChange={handleCityChange}
              disabled={!selectedValue.state || isLoadingCities}
            >
              <option value="">Select city</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {shouldShowArea ? (
          <label className="lsel__field">
            <span className="lsel__label">Area</span>
            <select
              className="lsel__select"
              value={selectedValue.area}
              onChange={handleAreaChange}
              disabled={!selectedValue.city || isLoadingAreas}
            >
              <option value="">Select area</option>
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    </div>
  );
};

export default LocationSelector;
